import React from "react";
import Image from "next/image";
import { ContentCard, Blocks } from "@/types/cms";
import { renderRichTextWithMutedStrikethrough } from "@/lib/richText";
import { getCmsImageUrl, toMediaNode } from "@/lib/cmsHelpers";
import styles from "./TestimonialCard.module.css";

interface TestimonialCardProps {
  card: ContentCard;
  body?: Blocks | string;
}

type BlockChild = { text?: string };
type Block = {
  type?: string;
  level?: number;
  children?: BlockChild[];
};

const TestimonialCard: React.FC<TestimonialCardProps> = ({ card, body }) => {
  const blocksFromCard = Array.isArray(card.content)
    ? card.content
    : Array.isArray(card.body)
      ? card.body
      : undefined;
  const overrideBlocks = Array.isArray(body) ? (body as Blocks) : undefined;
  const blocks = (overrideBlocks || blocksFromCard) as Block[] | undefined;

  const h1Block = blocks?.find((b) => b.type === "heading" && b.level === 1);
  const h2Block = blocks?.find((b) => b.type === "heading" && b.level === 2);
  const quoteBlocks = blocks?.filter(
    (b) =>
      b.type === "paragraph" ||
      (b.type === "heading" && b.level !== 1 && b.level !== 2)
  );

  const personName = h1Block?.children?.map((c: BlockChild) => c.text).join("") || "";
  const jobTitle = h2Block?.children?.map((c: BlockChild) => c.text).join("") || "";

  // Get image for avatar — routed through `getCmsImageUrl` so every
  // CMS response shape is normalized and the URL gets Cloudinary's
  // `q_auto:good,f_auto,w_...,c_scale` transforms applied before `<Image>`
  // re-optimizes it to WebP/AVIF.
  const cardMedia = card as ContentCard & { imageDesktop?: unknown };
  const avatarUrl =
    getCmsImageUrl(toMediaNode(cardMedia.imageDesktop)) ||
    getCmsImageUrl(toMediaNode(cardMedia.image)) ||
    getCmsImageUrl(toMediaNode(cardMedia.imageMobile));

  return (
    <div className={styles.card}>
      <div className={styles.quote}>
        {quoteBlocks ? renderRichTextWithMutedStrikethrough(quoteBlocks) : null}
      </div>
      {(avatarUrl || personName || jobTitle) && (
        <div className={styles.metaRow}>
          {avatarUrl && (
            <Image
              src={avatarUrl}
              alt={personName || "Avatar"}
              width={70}
              height={70}
              sizes="70px"
              className={styles.avatar}
            />
          )}
          <div className={styles.metaText}>
            {personName && (
              <p className={`body-sm ${styles.name}`}>{personName}</p>
            )}
            {jobTitle && (
              <p className={`body-md ${styles.title}`}>{jobTitle}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialCard;
